import {
  Chip,
  ChipTab,
  Link,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  WarningIcon,
} from '@all-manager/design-system-v2';
import { useRouterLinkClick } from "../../../hooks/useRouterLinkClick";
import FullPageLayout from "../../../layout/FullPageLayout/FullPageLayout";
import DocumentListInSubTopic from "../../documents/external-components/DocumentsListInSubTopic/DocumentListInSubTopic";
import ActivitiesTab from "../components/ActivitiesTab/ActivitiesTab";
import ActivityTimeline from "../components/ActivityTimeline/ActivityTimeline";
import AddActions from "../components/AddActions/AddActions";
import CommunicationsTab from "../components/CommunicationsTab/CommunicationsTab";
import EventsTab from "../components/EventsTab/EventsTab";
import IncidentsTab from "../components/IncidentsTab/IncidentsTab";
import NotesTab from "../components/NotesTab/NotesTab";
import QuotesTab from "../components/QuotesTab/QuotesTab";
import ReminderTabContentWrapper from "../components/ReminderTabContentWrapper/ReminderTabContentWrapper";
import SubTopicActions from "../components/SubTopicActions/SubTopicActions";
import SubTopicState from "../components/SubTopicState/SubTopicState";
import { useSubTopicPage } from "./useSubTopicPage";

export default function SubTopicPage() {
  const {
    isAdmin,
    subTopicId,
    data,
    isLoading,
    hasOngoingIncident,
    ongoingIncidentsCount,
    activeTab,
    quotesDocsSubTab,
    journalSubTab,
    backLink,
    parentTopicUrl,
    parentTopicName,
    handleTabChange,
    handleQuotesDocsSubTabChange,
    handleJournalSubTabChange,
    navigateToIncidents,
    navigateToQuotes,
    navigateToJournal,
  } = useSubTopicPage();
  const handleParentTopicClick = useRouterLinkClick(parentTopicUrl ?? '');

  return (
    <FullPageLayout
      htmlTitle={data?.name ?? ''}
      backLink={backLink}
      title={data?.name ?? ''}
      status={
        data ? (
          <>
            <SubTopicState item={data} />
            {hasOngoingIncident ? (
              <Chip
                label="Incident en cours"
                size="small"
                icon={<WarningIcon fontSize="small" />}
                color="error"
                variant="outlined"
                onClick={navigateToIncidents}
              />
            ) : null}
          </>
        ) : null
      }
      subtitle={
        parentTopicName ? (
          parentTopicUrl ? (
            <Link
              href={parentTopicUrl}
              onClick={handleParentTopicClick}
              color="textSecondary"
              underline="hover"
            >
              {parentTopicName}
            </Link>
          ) : (
            parentTopicName
          )
        ) : null
      }
      subtitlePosition="top"
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          {isAdmin && subTopicId ? <AddActions subjectId={subTopicId} /> : null}
          {isAdmin && data ? <SubTopicActions subTopic={data} /> : null}
        </Stack>
      }
    >
      {isLoading ? (
        <Stack spacing={2} aria-busy="true" aria-label="Chargement du sous-sujet">
          <Skeleton variant="rectangular" height={48} />
          <Skeleton variant="rounded" height={160} />
          <Skeleton variant="rounded" height={160} />
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Tabs
            divider
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Sections du sous-sujet"
          >
            <Tab label="Dossier & Synthèse" value="dossier" />
            <Tab label="Documents & Devis" value="quotes-docs" />
            <Tab label="Journal & Échanges" value="journal" />
          </Tabs>

          {activeTab === 'dossier' && subTopicId ? (
            <ActivitiesTab
              subTopicId={subTopicId}
              onNavigateToIncidents={navigateToIncidents}
              onNavigateToQuotes={navigateToQuotes}
              onNavigateToJournal={navigateToJournal}
            />
          ) : null}

          {activeTab === 'quotes-docs' ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} wrap="wrap" role="tablist" aria-label="Documents et devis">
                <ChipTab
                  label="Devis & BPA"
                  onClick={() => handleQuotesDocsSubTabChange('quotes')}
                  selected={quotesDocsSubTab === 'quotes'}
                />
                <ChipTab
                  label="Documents"
                  onClick={() => handleQuotesDocsSubTabChange('documents')}
                  selected={quotesDocsSubTab === 'documents'}
                />
              </Stack>

              {quotesDocsSubTab === 'quotes' && subTopicId ? (
                <QuotesTab subTopicId={subTopicId} />
              ) : quotesDocsSubTab === 'documents' ? (
                <DocumentListInSubTopic />
              ) : null}
            </Stack>
          ) : null}

          {activeTab === 'journal' ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} wrap="wrap" role="tablist" aria-label="Journal du sous-sujet">
                <ChipTab
                  label="Tout"
                  onClick={() => handleJournalSubTabChange('all')}
                  selected={journalSubTab === 'all'}
                />
                <ChipTab
                  label="Incidents"
                  count={ongoingIncidentsCount > 0 ? ongoingIncidentsCount : undefined}
                  onClick={() => handleJournalSubTabChange('incidents')}
                  selected={journalSubTab === 'incidents'}
                />
                <ChipTab
                  label="Rendez-vous"
                  onClick={() => handleJournalSubTabChange('events')}
                  selected={journalSubTab === 'events'}
                />
                <ChipTab
                  label="Communications"
                  onClick={() => handleJournalSubTabChange('communications')}
                  selected={journalSubTab === 'communications'}
                />
                <ChipTab
                  label="Rappels"
                  onClick={() => handleJournalSubTabChange('reminders')}
                  selected={journalSubTab === 'reminders'}
                />
                <ChipTab
                  label="Notes"
                  onClick={() => handleJournalSubTabChange('notes')}
                  selected={journalSubTab === 'notes'}
                />
                <ChipTab
                  label="Autres"
                  onClick={() => handleJournalSubTabChange('others')}
                  selected={journalSubTab === 'others'}
                />
              </Stack>

              {journalSubTab === 'all' && subTopicId ? (
                <ActivityTimeline subTopicId={subTopicId} showFilter={false} />
              ) : journalSubTab === 'events' && subTopicId ? (
                <EventsTab subTopicId={subTopicId} />
              ) : journalSubTab === 'incidents' && subTopicId ? (
                <IncidentsTab subTopicId={subTopicId} />
              ) : journalSubTab === 'communications' && subTopicId ? (
                <CommunicationsTab subTopicId={subTopicId} />
              ) : journalSubTab === 'reminders' && subTopicId ? (
                <ReminderTabContentWrapper topicId={subTopicId} />
              ) : journalSubTab === 'notes' && data ? (
                <NotesTab subTopic={data} />
              ) : journalSubTab === 'others' && subTopicId ? (
                <CommunicationsTab subTopicId={subTopicId} kind="others" />
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      )}
    </FullPageLayout>
  );
}
